// Vendored copy of object-inspect@1.13.4's index.js (version-pinned via the
// package.json `overrides` entry so this mirror cannot silently drift), with
// one spot patched: the `utilInspect`/`inspectCustom` block below.
// object-inspect (pulled in transitively via qs/side-channel into the vendor
// chunk) normally does `require('util').inspect.custom` at module scope to
// feature-detect Node's custom-inspect symbol. Under Rolldown's production
// build, `util` resolves to its own "externalized for browser" stub, which
// throws on *any* property access — crashing before React ever renders
// (blank screen, "Module '' has been externalized..." in the console).
// Rolldown treats recognized Node builtins specially before any plugin
// resolveId/resolve.alias hook runs, so aliasing `util` itself doesn't help
// (see vite.config.js) — the fix has to live in the file doing the unsafe
// access instead. Aliased via vite.config.js.
// Style (continue/for-in/++/nested ternaries/empty catches/etc.) is kept
// faithful to upstream rather than rewritten to this repo's airbnb rules —
// see the .eslintrc override for this file.
const hasMap = typeof Map === 'function' && Map.prototype;
const mapSizeDescriptor =
  Object.getOwnPropertyDescriptor && hasMap
    ? Object.getOwnPropertyDescriptor(Map.prototype, 'size')
    : null;
const mapSize =
  hasMap && mapSizeDescriptor && typeof mapSizeDescriptor.get === 'function'
    ? mapSizeDescriptor.get
    : null;
const mapForEach = hasMap && Map.prototype.forEach;
const hasSet = typeof Set === 'function' && Set.prototype;
const setSizeDescriptor =
  Object.getOwnPropertyDescriptor && hasSet
    ? Object.getOwnPropertyDescriptor(Set.prototype, 'size')
    : null;
const setSize =
  hasSet && setSizeDescriptor && typeof setSizeDescriptor.get === 'function'
    ? setSizeDescriptor.get
    : null;
const setForEach = hasSet && Set.prototype.forEach;
const hasWeakMap = typeof WeakMap === 'function' && WeakMap.prototype;
const weakMapHas = hasWeakMap ? WeakMap.prototype.has : null;
const hasWeakSet = typeof WeakSet === 'function' && WeakSet.prototype;
const weakSetHas = hasWeakSet ? WeakSet.prototype.has : null;
const hasWeakRef = typeof WeakRef === 'function' && WeakRef.prototype;
const weakRefDeref = hasWeakRef ? WeakRef.prototype.deref : null;
const booleanValueOf = Boolean.prototype.valueOf;
const objectToString = Object.prototype.toString;
const functionToString = Function.prototype.toString;
const $match = String.prototype.match;
const $slice = String.prototype.slice;
const $replace = String.prototype.replace;
const $toUpperCase = String.prototype.toUpperCase;
const $toLowerCase = String.prototype.toLowerCase;
const $test = RegExp.prototype.test;
const $concat = Array.prototype.concat;
const $join = Array.prototype.join;
const $arrSlice = Array.prototype.slice;
const $floor = Math.floor;
const bigIntValueOf =
  typeof BigInt === 'function' ? BigInt.prototype.valueOf : null;
const gOPS = Object.getOwnPropertySymbols;
const symToString =
  typeof Symbol === 'function' && typeof Symbol.iterator === 'symbol'
    ? Symbol.prototype.toString
    : null;
const hasShammedSymbols =
  typeof Symbol === 'function' && typeof Symbol.iterator === 'object';
// ie, `has-tostringtag/shams
const toStringTag =
  typeof Symbol === 'function' &&
  Symbol.toStringTag &&
  (typeof Symbol.toStringTag === hasShammedSymbols ? 'object' : 'symbol')
    ? Symbol.toStringTag
    : null;
const isEnumerable = Object.prototype.propertyIsEnumerable;

const gPO =
  (typeof Reflect === 'function'
    ? Reflect.getPrototypeOf
    : Object.getPrototypeOf) ||
  ([].__proto__ === Array.prototype // eslint-disable-line no-proto
    ? function(O) {
        return O.__proto__; // eslint-disable-line no-proto
      }
    : null);

function addNumericSeparator(num, str) {
  if (
    num === Infinity ||
    num === -Infinity ||
    num !== num ||
    (num && num > -1000 && num < 1000) ||
    $test.call(/e/, str)
  ) {
    return str;
  }
  const sepRegex = /[0-9](?=(?:[0-9]{3})+(?![0-9]))/g;
  if (typeof num === 'number') {
    const int = num < 0 ? -$floor(-num) : $floor(num); // trunc(num)
    if (int !== num) {
      const intStr = String(int);
      const dec = $slice.call(str, intStr.length + 1);
      return `${$replace.call(intStr, sepRegex, '$&_')}.${$replace.call(
        $replace.call(dec, /([0-9]{3})/g, '$&_'),
        /_$/,
        ''
      )}`;
    }
  }
  return $replace.call(str, sepRegex, '$&_');
}

// Patched (see file header): guard both the require and the `.custom`
// property access so a throwing externalized `util` degrades to `null`
// instead of crashing the whole app at module init.
let utilInspect;
try {
  utilInspect = require('util').inspect;
} catch (e) {}
let inspectCustom = null;
try {
  inspectCustom = utilInspect && utilInspect.custom;
} catch (e) {}
const inspectSymbol = isSymbol(inspectCustom) ? inspectCustom : null;

const quotes = {
  __proto__: null,
  double: '"',
  single: "'"
};
const quoteREs = {
  __proto__: null,
  double: /(["\\])/g,
  single: /(['\\])/g
};

module.exports = function inspect_(obj, options, depth, seen) {
  const opts = options || {};

  if (has(opts, 'quoteStyle') && !has(quotes, opts.quoteStyle)) {
    throw new TypeError('option "quoteStyle" must be "single" or "double"');
  }
  if (
    has(opts, 'maxStringLength') &&
    (typeof opts.maxStringLength === 'number'
      ? opts.maxStringLength < 0 && opts.maxStringLength !== Infinity
      : opts.maxStringLength !== null)
  ) {
    throw new TypeError(
      'option "maxStringLength", if provided, must be a positive integer, Infinity, or `null`'
    );
  }
  const customInspect = has(opts, 'customInspect') ? opts.customInspect : true;
  if (typeof customInspect !== 'boolean' && customInspect !== 'symbol') {
    throw new TypeError(
      'option "customInspect", if provided, must be `true`, `false`, or `\'symbol\'`'
    );
  }

  if (
    has(opts, 'indent') &&
    opts.indent !== null &&
    opts.indent !== '\t' &&
    !(parseInt(opts.indent, 10) === opts.indent && opts.indent > 0)
  ) {
    throw new TypeError(
      'option "indent" must be "\\t", an integer > 0, or `null`'
    );
  }
  if (
    has(opts, 'numericSeparator') &&
    typeof opts.numericSeparator !== 'boolean'
  ) {
    throw new TypeError(
      'option "numericSeparator", if provided, must be `true` or `false`'
    );
  }
  const { numericSeparator } = opts;

  if (typeof obj === 'undefined') {
    return 'undefined';
  }
  if (obj === null) {
    return 'null';
  }
  if (typeof obj === 'boolean') {
    return obj ? 'true' : 'false';
  }

  if (typeof obj === 'string') {
    return inspectString(obj, opts);
  }
  if (typeof obj === 'number') {
    if (obj === 0) {
      return Infinity / obj > 0 ? '0' : '-0';
    }
    const str = String(obj);
    return numericSeparator ? addNumericSeparator(obj, str) : str;
  }
  if (typeof obj === 'bigint') {
    const bigIntStr = `${String(obj)}n`;
    return numericSeparator ? addNumericSeparator(obj, bigIntStr) : bigIntStr;
  }

  const maxDepth = typeof opts.depth === 'undefined' ? 5 : opts.depth;
  if (typeof depth === 'undefined') {
    depth = 0;
  }
  if (depth >= maxDepth && maxDepth > 0 && typeof obj === 'object') {
    return isArray(obj) ? '[Array]' : '[Object]';
  }

  const indent = getIndent(opts, depth);

  if (typeof seen === 'undefined') {
    seen = [];
  } else if (indexOf(seen, obj) >= 0) {
    return '[Circular]';
  }

  function inspect(value, from, noIndent) {
    if (from) {
      seen = $arrSlice.call(seen);
      seen.push(from);
    }
    if (noIndent) {
      const newOpts = {
        depth: opts.depth
      };
      if (has(opts, 'quoteStyle')) {
        newOpts.quoteStyle = opts.quoteStyle;
      }
      return inspect_(value, newOpts, depth + 1, seen);
    }
    return inspect_(value, opts, depth + 1, seen);
  }

  if (typeof obj === 'function' && !isRegExp(obj)) {
    // in older engines, regexes are callable
    const name = nameOf(obj);
    const keys = arrObjKeys(obj, inspect);
    return `[Function${name ? `: ${name}` : ' (anonymous)'}]${
      keys.length > 0 ? ` { ${$join.call(keys, ', ')} }` : ''
    }`;
  }
  if (isSymbol(obj)) {
    const symString = hasShammedSymbols
      ? $replace.call(String(obj), /^(Symbol\(.*\))_[^)]*$/, '$1')
      : symToString.call(obj);
    return typeof obj === 'object' && !hasShammedSymbols
      ? markBoxed(symString)
      : symString;
  }
  if (isElement(obj)) {
    let s = `<${$toLowerCase.call(String(obj.nodeName))}`;
    const attrs = obj.attributes || [];
    for (let i = 0; i < attrs.length; i++) {
      s += ` ${attrs[i].name}=${wrapQuotes(
        quote(attrs[i].value),
        'double',
        opts
      )}`;
    }
    s += '>';
    if (obj.childNodes && obj.childNodes.length) {
      s += '...';
    }
    s += `</${$toLowerCase.call(String(obj.nodeName))}>`;
    return s;
  }
  if (isArray(obj)) {
    if (obj.length === 0) {
      return '[]';
    }
    const xs = arrObjKeys(obj, inspect);
    if (indent && !singleLineValues(xs)) {
      return `[${indentedJoin(xs, indent)}]`;
    }
    return `[ ${$join.call(xs, ', ')} ]`;
  }
  if (isError(obj)) {
    const parts = arrObjKeys(obj, inspect);
    if (
      !('cause' in Error.prototype) &&
      'cause' in obj &&
      !isEnumerable.call(obj, 'cause')
    ) {
      return `{ [${String(obj)}] ${$join.call(
        $concat.call(`[cause]: ${inspect(obj.cause)}`, parts),
        ', '
      )} }`;
    }
    if (parts.length === 0) {
      return `[${String(obj)}]`;
    }
    return `{ [${String(obj)}] ${$join.call(parts, ', ')} }`;
  }
  if (typeof obj === 'object' && customInspect) {
    if (
      inspectSymbol &&
      typeof obj[inspectSymbol] === 'function' &&
      utilInspect
    ) {
      return utilInspect(obj, { depth: maxDepth - depth });
    }
    if (customInspect !== 'symbol' && typeof obj.inspect === 'function') {
      return obj.inspect();
    }
  }
  if (isMap(obj)) {
    const mapParts = [];
    if (mapForEach) {
      mapForEach.call(obj, function(value, key) {
        mapParts.push(`${inspect(key, obj, true)} => ${inspect(value, obj)}`);
      });
    }
    return collectionOf('Map', mapSize.call(obj), mapParts, indent);
  }
  if (isSet(obj)) {
    const setParts = [];
    if (setForEach) {
      setForEach.call(obj, function(value) {
        setParts.push(inspect(value, obj));
      });
    }
    return collectionOf('Set', setSize.call(obj), setParts, indent);
  }
  if (isWeakMap(obj)) {
    return weakCollectionOf('WeakMap');
  }
  if (isWeakSet(obj)) {
    return weakCollectionOf('WeakSet');
  }
  if (isWeakRef(obj)) {
    return weakCollectionOf('WeakRef');
  }
  if (isNumber(obj)) {
    return markBoxed(inspect(Number(obj)));
  }
  if (isBigInt(obj)) {
    return markBoxed(inspect(bigIntValueOf.call(obj)));
  }
  if (isBoolean(obj)) {
    return markBoxed(booleanValueOf.call(obj));
  }
  if (isString(obj)) {
    return markBoxed(inspect(String(obj)));
  }
  // note: in IE 8, sometimes `global !== window` but both are the prototypes of each other
  /* eslint-env browser */
  if (typeof window !== 'undefined' && obj === window) {
    return '{ [object Window] }';
  }
  if (
    (typeof globalThis !== 'undefined' && obj === globalThis) ||
    (typeof global !== 'undefined' && obj === global)
  ) {
    return '{ [object globalThis] }';
  }
  if (!isDate(obj) && !isRegExp(obj)) {
    const ys = arrObjKeys(obj, inspect);
    const isPlainObject = gPO
      ? gPO(obj) === Object.prototype
      : obj instanceof Object || obj.constructor === Object;
    const protoTag = obj instanceof Object ? '' : 'null prototype';
    const stringTag =
      !isPlainObject && toStringTag && Object(obj) === obj && toStringTag in obj
        ? $slice.call(toStr(obj), 8, -1)
        : protoTag
        ? 'Object'
        : '';
    const constructorTag =
      isPlainObject || typeof obj.constructor !== 'function'
        ? ''
        : obj.constructor.name
        ? `${obj.constructor.name} `
        : '';
    const tag =
      constructorTag +
      (stringTag || protoTag
        ? `[${$join.call(
            $concat.call([], stringTag || [], protoTag || []),
            ': '
          )}] `
        : '');
    if (ys.length === 0) {
      return `${tag}{}`;
    }
    if (indent) {
      return `${tag}{${indentedJoin(ys, indent)}}`;
    }
    return `${tag}{ ${$join.call(ys, ', ')} }`;
  }
  return String(obj);
};

function wrapQuotes(s, defaultStyle, opts) {
  const style = opts.quoteStyle || defaultStyle;
  const quoteChar = quotes[style];
  return quoteChar + s + quoteChar;
}

function quote(s) {
  return $replace.call(String(s), /"/g, '&quot;');
}

function canTrustToString(obj) {
  return (
    !toStringTag ||
    !(
      typeof obj === 'object' &&
      (toStringTag in obj || typeof obj[toStringTag] !== 'undefined')
    )
  );
}
function isArray(obj) {
  return toStr(obj) === '[object Array]' && canTrustToString(obj);
}
function isDate(obj) {
  return toStr(obj) === '[object Date]' && canTrustToString(obj);
}
function isRegExp(obj) {
  return toStr(obj) === '[object RegExp]' && canTrustToString(obj);
}
function isError(obj) {
  return toStr(obj) === '[object Error]' && canTrustToString(obj);
}
function isString(obj) {
  return toStr(obj) === '[object String]' && canTrustToString(obj);
}
function isNumber(obj) {
  return toStr(obj) === '[object Number]' && canTrustToString(obj);
}
function isBoolean(obj) {
  return toStr(obj) === '[object Boolean]' && canTrustToString(obj);
}

// Symbol and BigInt do have Symbol.toStringTag by spec, so that can't be used to eliminate false positives
function isSymbol(obj) {
  if (hasShammedSymbols) {
    return obj && typeof obj === 'object' && obj instanceof Symbol;
  }
  if (typeof obj === 'symbol') {
    return true;
  }
  if (!obj || typeof obj !== 'object' || !symToString) {
    return false;
  }
  try {
    symToString.call(obj);
    return true;
  } catch (e) {}
  return false;
}

function isBigInt(obj) {
  if (!obj || typeof obj !== 'object' || !bigIntValueOf) {
    return false;
  }
  try {
    bigIntValueOf.call(obj);
    return true;
  } catch (e) {}
  return false;
}

const hasOwn =
  Object.prototype.hasOwnProperty ||
  function(key) {
    return key in this;
  };
function has(obj, key) {
  return hasOwn.call(obj, key);
}

function toStr(obj) {
  return objectToString.call(obj);
}

function nameOf(f) {
  if (f.name) {
    return f.name;
  }
  const m = $match.call(functionToString.call(f), /^function\s*([\w$]+)/);
  if (m) {
    return m[1];
  }
  return null;
}

function indexOf(xs, x) {
  if (xs.indexOf) {
    return xs.indexOf(x);
  }
  for (let i = 0, l = xs.length; i < l; i++) {
    if (xs[i] === x) {
      return i;
    }
  }
  return -1;
}

function isMap(x) {
  if (!mapSize || !x || typeof x !== 'object') {
    return false;
  }
  try {
    mapSize.call(x);
    try {
      setSize.call(x);
    } catch (s) {
      return true;
    }
    return x instanceof Map; // core-js workaround, pre-v2.5.0
  } catch (e) {}
  return false;
}

function isWeakMap(x) {
  if (!weakMapHas || !x || typeof x !== 'object') {
    return false;
  }
  try {
    weakMapHas.call(x, weakMapHas);
    try {
      weakSetHas.call(x, weakSetHas);
    } catch (s) {
      return true;
    }
    return x instanceof WeakMap; // core-js workaround, pre-v2.5.0
  } catch (e) {}
  return false;
}

function isWeakRef(x) {
  if (!weakRefDeref || !x || typeof x !== 'object') {
    return false;
  }
  try {
    weakRefDeref.call(x);
    return true;
  } catch (e) {}
  return false;
}

function isSet(x) {
  if (!setSize || !x || typeof x !== 'object') {
    return false;
  }
  try {
    setSize.call(x);
    try {
      mapSize.call(x);
    } catch (m) {
      return true;
    }
    return x instanceof Set; // core-js workaround, pre-v2.5.0
  } catch (e) {}
  return false;
}

function isWeakSet(x) {
  if (!weakSetHas || !x || typeof x !== 'object') {
    return false;
  }
  try {
    weakSetHas.call(x, weakSetHas);
    try {
      weakMapHas.call(x, weakMapHas);
    } catch (s) {
      return true;
    }
    return x instanceof WeakSet; // core-js workaround, pre-v2.5.0
  } catch (e) {}
  return false;
}

function isElement(x) {
  if (!x || typeof x !== 'object') {
    return false;
  }
  if (typeof HTMLElement !== 'undefined' && x instanceof HTMLElement) {
    return true;
  }
  return typeof x.nodeName === 'string' && typeof x.getAttribute === 'function';
}

function inspectString(str, opts) {
  if (str.length > opts.maxStringLength) {
    const remaining = str.length - opts.maxStringLength;
    const trailer = `... ${remaining} more character${
      remaining > 1 ? 's' : ''
    }`;
    return (
      inspectString($slice.call(str, 0, opts.maxStringLength), opts) + trailer
    );
  }
  const quoteRE = quoteREs[opts.quoteStyle || 'single'];
  quoteRE.lastIndex = 0;
  // eslint-disable-next-line no-control-regex
  const s = $replace.call(
    $replace.call(str, quoteRE, '\\$1'),
    /[\x00-\x1f]/g,
    lowbyte
  );
  return wrapQuotes(s, 'single', opts);
}

function lowbyte(c) {
  const n = c.charCodeAt(0);
  const x = {
    8: 'b',
    9: 't',
    10: 'n',
    12: 'f',
    13: 'r'
  }[n];
  if (x) {
    return `\\${x}`;
  }
  return `\\x${n < 0x10 ? '0' : ''}${$toUpperCase.call(n.toString(16))}`;
}

function markBoxed(str) {
  return `Object(${str})`;
}

function weakCollectionOf(type) {
  return `${type} { ? }`;
}

function collectionOf(type, size, entries, indent) {
  const joinedEntries = indent
    ? indentedJoin(entries, indent)
    : $join.call(entries, ', ');
  return `${type} (${size}) {${joinedEntries}}`;
}

function singleLineValues(xs) {
  for (let i = 0; i < xs.length; i++) {
    if (indexOf(xs[i], '\n') >= 0) {
      return false;
    }
  }
  return true;
}

function getIndent(opts, depth) {
  let baseIndent;
  if (opts.indent === '\t') {
    baseIndent = '\t';
  } else if (typeof opts.indent === 'number' && opts.indent > 0) {
    baseIndent = $join.call(Array(opts.indent + 1), ' ');
  } else {
    return null;
  }
  return {
    base: baseIndent,
    prev: $join.call(Array(depth + 1), baseIndent)
  };
}

function indentedJoin(xs, indent) {
  if (xs.length === 0) {
    return '';
  }
  const lineJoiner = `\n${indent.prev}${indent.base}`;
  return `${lineJoiner + $join.call(xs, `,${lineJoiner}`)}\n${indent.prev}`;
}

function arrObjKeys(obj, inspect) {
  const isArr = isArray(obj);
  const xs = [];
  if (isArr) {
    xs.length = obj.length;
    for (let i = 0; i < obj.length; i++) {
      xs[i] = has(obj, i) ? inspect(obj[i], obj) : '';
    }
  }
  const syms = typeof gOPS === 'function' ? gOPS(obj) : [];
  let symMap;
  if (hasShammedSymbols) {
    symMap = {};
    for (let k = 0; k < syms.length; k++) {
      symMap[`$${syms[k]}`] = syms[k];
    }
  }

  for (const key in obj) {
    // eslint-disable-line no-restricted-syntax
    if (!has(obj, key)) {
      continue;
    } // eslint-disable-line no-restricted-syntax, no-continue
    if (isArr && String(Number(key)) === key && key < obj.length) {
      continue;
    } // eslint-disable-line no-restricted-syntax, no-continue
    if (hasShammedSymbols && symMap[`$${key}`] instanceof Symbol) {
      // this is to prevent shammed Symbols, which are stored as strings, from being included in the string key section
      continue; // eslint-disable-line no-restricted-syntax, no-continue
    } else if ($test.call(/[^\w$]/, key)) {
      xs.push(`${inspect(key, obj)}: ${inspect(obj[key], obj)}`);
    } else {
      xs.push(`${key}: ${inspect(obj[key], obj)}`);
    }
  }
  if (typeof gOPS === 'function') {
    for (let j = 0; j < syms.length; j++) {
      if (isEnumerable.call(obj, syms[j])) {
        xs.push(`[${inspect(syms[j])}]: ${inspect(obj[syms[j]], obj)}`);
      }
    }
  }
  return xs;
}
